import { IconButton } from '@creditornot/cb-button';
import { InputCheckbox } from '@creditornot/cb-checkbox';
import { DragHandle, MoreHorizontal } from '@creditornot/cb-icons';
import { colors, cbSemanticCssVars, WoltLogo } from '@creditornot/cb-ingredients';
import { MenuItemGroup, MenuLinkItem } from '@creditornot/cb-menu';
import { Popover } from '@creditornot/cb-popover';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { ChangeEvent, CSSProperties, useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { useLocaleUtils } from 'common/hooks';
import { Typography as T } from 'common/typography';
import { localize } from 'common/utils';
import { MenuLocaleContext } from 'modules/menu/MenuLocaleContext';
import LazyLoadedThumbnail from 'modules/menu/components/LazyLoadedThumbnail';
import { solidify } from 'modules/menu/solidify';

import { UNASSIGNED_AREA_ID } from '../constants';
import useListItemInfo from '../hooks/useListItemInfo';
import { DnD, ItemData } from '../types';

type Props = {
  className?: string;
  itemData: ItemData;
  storageLocation: string | null;
  canSort?: boolean;
  selectedItemIds: string[];
  style?: CSSProperties;
  onCheckItem?: (event: ChangeEvent<HTMLInputElement>) => void;
  onMoveItem: (itemData: ItemData) => void;
  onRemoveItem?: (itemId: string) => void;
};

const Root = styled.div<{ checked?: boolean; isDragging: boolean }>`
  display: flex;
  justify-content: space-between;
  height: 104px;
  padding: 24px;
  background-color: ${({ checked, theme }) =>
    checked ? solidify(colors.wolt4, theme.cbThemeMode) : cbSemanticCssVars.surfaceMain};
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  /* dnd-kit is setting opacity of sortable items to zero, so we need to overwrite it */
  opacity: ${({ isDragging }) => (isDragging ? 0.4 : 1)} !important;
`;

const LeftContent = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  align-items: center;
`;

const RightContent = styled.div`
  display: flex;
  align-items: center;
`;

const StyledDragHandle = styled(DragHandle)`
  flex-shrink: 0;
  width: 16px;
  fill: ${cbSemanticCssVars.textPrimary};
  margin-inline-end: 16px;
  cursor: grab;
`;

const ImageContainer = styled.div<{ bordered?: boolean }>`
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 56px;
  height: 56px;
  border-radius: 8px;
  margin-inline-start: 16px;
  background-color: ${cssThemedValue({ light: colors.pepper8, dark: colors.salt12 })};
`;

const PlaceholderContainer = styled(ImageContainer)`
  border: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const PlaceholderImage = styled(WoltLogo)`
  width: 36px;
  fill: ${cbSemanticCssVars.textTertiary};
`;

const InfoContainer = styled.div<{ verticallyCentered: boolean }>`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: ${({ verticallyCentered }) => (verticallyCentered ? 'center' : 'space-between')};
  margin-inline-start: 16px;
  overflow: hidden;
`;

const ItemName = styled(T.Body3)`
  color: ${cbSemanticCssVars.textPrimary};
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemInfo = styled(T.Body3)`
  color: ${cbSemanticCssVars.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const OriginalPrice = styled(T.Body3)<{ lineThrough?: boolean }>`
  color: ${cbSemanticCssVars.textPrimary};
  margin: 0 16px;
  text-decoration: ${({ lineThrough }) => (lineThrough ? 'line-through' : 'none')};
`;

const DiscountedPrice = styled(T.Body3)`
  color: ${colors.strawberry100};
  margin-inline-start: 16px;
`;

const PopoverContent = styled(MenuItemGroup)`
  padding: 0 8px;
`;

const ItemRow = ({
  className,
  itemData,
  storageLocation,
  canSort,
  selectedItemIds,
  style,
  onCheckItem,
  onMoveItem,
  onRemoveItem,
}: Props) => {
  const { data: item, areaId } = itemData;

  const { primaryLanguage } = useContext(MenuLocaleContext);

  const { getLocalizedUnit } = useLocaleUtils();
  const { formatItemInfo } = useListItemInfo(storageLocation);

  const itemName = localize(item.name, primaryLanguage);
  const itemInfo = formatItemInfo(item);
  const { currency } = item.baseprice;

  const checked = selectedItemIds?.includes(item.id);

  const sortable = useSortable({
    id: item.id,
    disabled: { droppable: !canSort },
    data:
      selectedItemIds.length > 1 && selectedItemIds.includes(item.id)
        ? { type: DnD.MultiItems, itemIds: selectedItemIds }
        : { type: DnD.SingleItem },
  });

  const dndStyle: CSSProperties = useMemo(
    () => ({
      ...style,
      transform: CSS.Transform.toString(sortable.transform),
      transition: sortable.transition,
    }),
    [sortable.transform, sortable.transition, style],
  );

  return (
    <Root
      className={className}
      ref={sortable.setNodeRef}
      style={dndStyle}
      {...sortable.attributes}
      {...sortable.listeners}
      isDragging={sortable.isDragging}
      checked={checked}
    >
      <LeftContent>
        {canSort && <StyledDragHandle />}
        <InputCheckbox checked={checked} onChange={onCheckItem} readOnly={!onCheckItem} />
        {item.image ? (
          <ImageContainer>
            <LazyLoadedThumbnail alt={itemName} blurhash={item.image_blurhash} src={item.image} />
          </ImageContainer>
        ) : (
          <PlaceholderContainer>
            <PlaceholderImage />
          </PlaceholderContainer>
        )}
        <InfoContainer verticallyCentered={!itemInfo}>
          <ItemName>{itemName}</ItemName>
          {itemInfo && <ItemInfo>{itemInfo}</ItemInfo>}
        </InfoContainer>
      </LeftContent>
      <RightContent>
        {item.discounted && (
          <DiscountedPrice>
            <MonetaryAmount amount={parseFloat(item.baseprice.amount)} currency={currency} />
            {!!item.sell_by_weight_config && `/${getLocalizedUnit('kilogram')}`}
          </DiscountedPrice>
        )}
        <OriginalPrice lineThrough={!!item.discounted}>
          <MonetaryAmount
            amount={parseFloat(item.discounted ? item.discounted.original_price.amount : item.baseprice.amount)}
            currency={currency}
          />
          {!!item.sell_by_weight_config && `/${getLocalizedUnit('kilogram')}`}
        </OriginalPrice>
        <Popover
          showOnClick
          showOnHover={false}
          renderArrow={false}
          portalElement={null}
          contentZIndex={1}
          placement="bottom-start"
          closeOnClickInside
          content={
            <PopoverContent>
              <MenuLinkItem variant="black" onClick={() => onMoveItem(itemData)}>
                <FormattedMessage id="picking-route.item-action-move" />
              </MenuLinkItem>
              {areaId !== UNASSIGNED_AREA_ID && onRemoveItem && (
                <MenuLinkItem variant="red" onClick={() => onRemoveItem(item.id)}>
                  <FormattedMessage id="picking-route.item-action-remove" />
                </MenuLinkItem>
              )}
            </PopoverContent>
          }
        >
          <IconButton variant="blue" size="tiny">
            <MoreHorizontal />
          </IconButton>
        </Popover>
      </RightContent>
    </Root>
  );
};

export default ItemRow;
